/* 시공사례 화면 공통 클래스 — 별도 CSS 없이 ui-theme Tailwind 토큰 유틸만 쓴다(D-07 §3.1, ui-lint).
   여러 컴포넌트가 같은 모양을 쓰는 곳만 여기 모은다. 한 곳에서만 쓰는 모양은 그 자리에 바로 적는다. */
export const cx = (...c: unknown[]) => c.filter(Boolean).join(' ');

/** 문서(페이지) 판형 — 내보낸 사진(1080×1440)·공개 페이지와 같은 좌표계 */
export const PAGE_BOX = { width: 720, height: 960 } as const;

export const hint = 'm-0 text-caption leading-relaxed text-text-secondary';
export const chipRow = 'flex flex-wrap items-center gap-sm';
/** 정사각 아이콘 버튼(Button size="sm" 과 같이) */
export const iconBtn = 'w-[var(--button-height-sm)] px-0';
export const paneTitle = 'm-0 text-heading-sm';
export const groupTitle = 'mb-02 mt-03 text-caption font-semibold text-text-secondary';
export const sec = 'mt-04';
export const textOk = 'text-[var(--status-success-700)]';
export const textWarn = 'text-[var(--status-warning-700)]';
/** 떠 있는 작은 창(메뉴·팝오버) */
export const popover = 'fixed z-dropdown flex flex-col gap-sm rounded-lg border border-border bg-surface-raised p-04 text-text shadow-[var(--elevation-2)]';
export const popHead = 'flex items-center justify-between gap-sm [&>b]:text-heading-sm';
export const popTitle = 'text-caption font-semibold text-text-secondary';
export const popNote = 'm-0 border-t border-border pt-03 text-caption leading-relaxed text-text-secondary';
/** ItemList 를 메뉴처럼 — 안쪽 여백만 줄인다 */
export const menuList = 'rounded-md px-04';
export const rowHint = 'whitespace-normal text-caption leading-normal text-text-secondary';
/** 세로 구분선 (Divider 를 세로로) */
export const vDivider = 'mx-02 my-0 h-06 w-px shrink-0';
/** 카드 hover·focus — Card(.app-card) 를 누르는 카드로 쓸 때 */
export const cardPress = 'cursor-pointer hover:border-group hover:shadow-[var(--elevation-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-group';
/** 사진 위에 얹는 어두운 라벨 */
export const photoLabel = 'rounded-sm bg-[var(--alpha-black-600)] px-02 text-caption text-[var(--gray-0)]';
/** 그림 타일(temp_TileButton) 공통 */
export const tile = 'rounded-md';
export const tileMuted = 'rounded-md bg-surface hover:bg-surface-raised';

/** 고를 수 없을 때 살짝 흔들기 — CSS 키프레임 대신 Web Animations. 움직임 줄이기 설정이면 하지 않는다 */
export function shake(el: Element | null | undefined, times = 1) {
  if (!el || typeof el.animate !== 'function' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const d = getComputedStyle(document.documentElement).getPropertyValue('--spacing-02').trim() || '0';
  el.animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${d})` }, { transform: `translateX(${d})` }, { transform: 'translateX(0)' }],
    { duration: 350, iterations: times });
}
/** 숫자가 바뀌었을 때 살짝 커졌다 돌아오기 */
export function pulse(el: Element | null | undefined) {
  if (!el || typeof el.animate !== 'function' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }], { duration: 600 });
}

/** 큰 팝업(템플릿·표시하고 고치기) — Modal 상자를 화면 높이에 맞추고, 본문은 머리·몸통·발 세로 배치 */
export const dialogBox = 'flex h-[calc(100vh-var(--spacing-10))] flex-col [&>.modal-body]:flex [&>.modal-body]:min-h-0 [&>.modal-body]:flex-1 [&>.modal-body]:flex-col [&>.modal-body]:p-0 [&>.modal-body]:text-body-sm [&>.modal-body]:text-text';
export const footMeta = 'm-0 min-w-0 text-body-sm leading-relaxed text-text-secondary [&_b]:font-semibold [&_b]:text-text';
export const modeList = 'mx-auto grid max-w-page-md grid-cols-[repeat(auto-fit,minmax(calc(var(--spacing-13)+var(--spacing-12)+var(--spacing-04)),1fr))] gap-md';

/** 사진 모양 견본(요소 탭·사진 모양 팝오버) — 모양 키별 클래스 */
const CLIP: Record<string, string> = {
  round: 'rounded-md', circle: 'rounded-full', arch: 'rounded-t-xl',
  hexagon: '[clip-path:polygon(25%_0,75%_0,100%_50%,75%_100%,25%_100%,0_50%)]',
  diamond: '[clip-path:polygon(50%_0,100%_50%,50%_100%,0_50%)]',
};
export const clipSample = (k: string) => cx('block size-07 bg-gradient-to-br from-[var(--warm-peach-300)] to-[var(--warm-peach-700)]', CLIP[k]);
/** 도형·선·사진 모양 타일 */
export const elTile = (selected = false, line = false) => cx('grid place-items-center rounded-md p-03 [&_svg]:size-full', line ? 'h-09' : 'aspect-square',
  selected ? 'bg-surface-raised' : 'bg-surface hover:bg-surface-raised');

/* ── 시공사례 카드(홈·블로그로 퍼가기가 같이 씀) — 페이지 썸네일 자체를 카드로, 이름은 아래에(테두리 박스 없이) ── */
export const CARD = 'flex min-w-0 flex-col gap-02 text-left';
export const CARD_THUMB = 'relative overflow-hidden rounded-lg border border-border bg-surface-raised shadow-[var(--elevation-1)] transition-shadow group-hover:border-group group-hover:shadow-[var(--elevation-2)]';
/** 카드 격자 — 창 폭을 꽉 채운다(카드 최소 폭 192 = spacing-13 + spacing-07, 남는 폭은 카드가 나눠 커진다) */
export const CARD_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing-13)+var(--spacing-07)),1fr))] gap-x-md gap-y-lg';
/** 카드 전체를 누르는 카드 — Enter·Space 로도 열린다 */
export const pressable = (open: () => void) => ({
  role: 'button', tabIndex: 0, onClick: open,
  onKeyDown: (e: { key: string; target: unknown; currentTarget: unknown; preventDefault: () => void }) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); open(); }
  },
});
export function relTime(iso: string) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return '방금';
  if (s < 3600) return Math.floor(s / 60) + '분 전';
  if (s < 86400) return Math.floor(s / 3600) + '시간 전';
  if (s < 86400 * 7) return Math.floor(s / 86400) + '일 전';
  return new Date(iso).toLocaleDateString('ko-KR');
}
